import React from "react";
import { Field } from "redux-form";
import PropTypes from "prop-types";
import {
  RenderInput,
  RenderSelect,
  RenderRadio,
} from "../../Common/InputComponents/Inputs.jsx";
import {
  required,
  requiredTarget,
  requiredSeq,
  maxLength17,
  minLength5,
  hasValidWhitespace,
  configureHdrContextSequenceValidator,
  nullValidationForServerErrors,
} from "../../../util/validations.js";
import { firstSeventeen, removeSpaces } from "../../../util/format";
import {
  tooltipReferenceGenomeOption,
  tooltipReferenceGenomeMatchReference,
  tooltipGenomeEditDetectionHdrContextSequence,
  tooltipGenomeEditDetectionHdrDonorSequence,
  tooltipGenomeEditDetectionSpecies,
  tooltipGenomeEditDetectionNhejContextSequence,
  tooltipGenomeEditDetectionNhejDonorPresent,
} from "../../Common/Misc/Tooltips.jsx";

const genomeOptions = [
  "Homo sapiens",
  "Mus musculus",
  "Rattus norvegicus",
  "Other",
];

const sequenceVariation = (species) => {
  return (
    <span>
      The sequence being edited <strong>is a variation</strong> of the {species}{" "}
      reference genome
    </span>
  );
};

const sequenceMatches = (species) => {
  return (
    <span>
      The sequence being edited <strong>matches</strong> the {species} reference
      genome
    </span>
  );
};

export const GenomeEditDetectionHDR = ({ fields, custom }) => {
  const validatedAssayDetails = (custom && custom.validatedAssayDetails) || {};

  return (
    <div>
      {fields.map((genomeEditDetectionHDR, index) => {
        const validationErrorDetails = validatedAssayDetails[index] || {};
        const selectedSpecies =
          custom.formObj &&
          custom.formObj.values &&
          custom.formObj.values.genomeEditDetectionHDR &&
          custom.formObj.values.genomeEditDetectionHDR[index].species;

        const sequenceVariationType =
          custom.formObj &&
          custom.formObj.values &&
          custom.formObj.values.genomeEditDetectionHDR &&
          custom.formObj.values.genomeEditDetectionHDR[index]
            .sequenceVariationType;
        const donorPresent =
          custom.formObj &&
          custom.formObj.values &&
          custom.formObj.values.genomeEditDetectionHDR &&
          custom.formObj.values.genomeEditDetectionHDR[index].donorPresent;

        const contextSequenceRegEx = /contextsequence/i;
        const donorSequenceRegEx = /donorsequence/i;
        const contextSequenceErrors =
          (nullValidationForServerErrors(
            validationErrorDetails,
            "GenomeEditDetection",
            "HdrGenomeEdit"
          ) &&
            validationErrorDetails.Errors[0].Property &&
            contextSequenceRegEx.test(
              validationErrorDetails.Errors[0].Property
            ) &&
            validationErrorDetails) ||
          {};
        const donorSequenceErrors =
          (nullValidationForServerErrors(
            validationErrorDetails,
            "GenomeEditDetection",
            "HdrGenomeEdit"
          ) &&
            validationErrorDetails.Errors[0].Property &&
            donorSequenceRegEx.test(
              validationErrorDetails.Errors[0].Property
            ) &&
            validationErrorDetails) ||
          {};

        const sequenceVariationVisibility =
          selectedSpecies && selectedSpecies !== "Other";

        const donorSequenceVisibility =
          sequenceVariationType !== undefined || selectedSpecies === "Other";

        const contextSequenceVisibility =
          (sequenceVariationType && sequenceVariationType === "false") ||
          selectedSpecies === "Other";

        const contextSequenceRelationVisibility =
          selectedSpecies !== "Other" && sequenceVariationType === "false";
          
        const clearValidationMessageOnChange = () => {
          custom.actions.clearValidationMessages(index);
        };
        return (
          <div key={index} className="inputs">
            {/* Assay Target Name for the  Nhej input*/}
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <Field
                    name={`${genomeEditDetectionHDR}.assayTargetName`}
                    placeholder="17 chars. maximum"
                    className="form-control"
                    type="text"
                    component={RenderInput}
                    label="Identifier:"
                    validate={[requiredTarget, hasValidWhitespace, minLength5]}
                    normalize={firstSeventeen}
                  />
                </div>
              </div>
              <div className="col" />
            </div>

            {/* Species for the  Nhej input*/}
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <Field
                    name={`${genomeEditDetectionHDR}.species`}
                    className="form-control"
                    component={RenderSelect}
                    label="Species:"
                    tooltip={tooltipGenomeEditDetectionSpecies}
                    validate={[]}
                  >
                    {genomeOptions &&
                      genomeOptions.map((option) => {
                        return (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        );
                      })}
                  </Field>
                </div>
              </div>
              <div className="col" />
            </div>

            {/*sequenceVariationType for the edit sequence*/}
            {sequenceVariationVisibility && (
              <div className="row">
                <div className="col">
                  <div id="sequenceVariationTypeContainer">
                    <Field
                      name={`${genomeEditDetectionHDR}.sequenceVariationType`}
                      label="Choose an option:"
                      component={RenderRadio}
                      tooltip={tooltipReferenceGenomeMatchReference}
                      options={{
                        true: sequenceMatches(selectedSpecies),
                        false: sequenceVariation(selectedSpecies),
                      }}
                    />
                  </div>
                </div>
                <div className="col" />
              </div>
            )}

            {/*Context Sequence for HDR*/}
            {contextSequenceVisibility && (
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <Field
                      name={`${genomeEditDetectionHDR}.contextSequence`}
                      className="form-control"
                      type="textArea"
                      rows="8"
                      placeholder="Enter the unedited sequence of the region of the genome that your edit targets. Cut site annotation is not required."
                      component={RenderInput}
                      label="Context Sequence:"
                      validate={[]}
                      tooltip={tooltipGenomeEditDetectionHdrContextSequence}
                      normalize={removeSpaces}
                      serverValidation={contextSequenceErrors}
                      onChange={clearValidationMessageOnChange}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* contextSequenceRelation option for the edit sequence*/}
            {contextSequenceRelationVisibility && (
              <div className="row">
                <div className="col">
                  <Field
                    name={`${genomeEditDetectionHDR}.contextSequenceRelation`}
                    label="This Context Sequence:"
                    component={RenderRadio}
                    tooltip={tooltipReferenceGenomeOption}
                    validate={[requiredSeq]}
                    options={{
                      HomologousVariant:
                        "Replaces the homologous sequence in the background genome",
                      Insertion: "Is an insertion into the background genome",
                    }}
                  />
                </div>

                <div className="col" />
              </div>
            )}
            <br />

            {/*donorSequence for the HDR*/}
            {donorSequenceVisibility && (
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <Field
                      name={`${genomeEditDetectionHDR}.donorSequence`}
                      className="form-control"
                      type="textArea"
                      rows="8"
                      placeholder="Enter Donor Sequence here."
                      component={RenderInput}
                      label="Donor Sequence:"
                      tooltip={tooltipGenomeEditDetectionHdrDonorSequence}
                      validate={[requiredSeq]}
                      normalize={removeSpaces}
                      serverValidation={donorSequenceErrors}
                      onChange={clearValidationMessageOnChange}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

GenomeEditDetectionHDR.propTypes = {
  fields: PropTypes.object.isRequired,
};
